/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AIExtractionContextCreate } from '../models/AIExtractionContextCreate';
import type { AIExtractionContextRead } from '../models/AIExtractionContextRead';
import type { AutoMappingAIPayload } from '../models/AutoMappingAIPayload';
import type { BackfillMappingPayload } from '../models/BackfillMappingPayload';
import type { CopiedRichTextPayload } from '../models/CopiedRichTextPayload';
import type { CopiedRichTextResponse } from '../models/CopiedRichTextResponse';
import type { DataExtractionOverview } from '../models/DataExtractionOverview';
import type { DataExtractionPayload } from '../models/DataExtractionPayload';
import type { DataExtractionPresenceRead } from '../models/DataExtractionPresenceRead';
import type { DataExtractionResults } from '../models/DataExtractionResults';
import type { DataExtractionSummary } from '../models/DataExtractionSummary';
import type { DuplicateLensPayload } from '../models/DuplicateLensPayload';
import type { ExtractionQueryAttemptRead } from '../models/ExtractionQueryAttemptRead';
import type { FieldDiscoveryStartResponse } from '../models/FieldDiscoveryStartResponse';
import type { GenerateMappingsPayload } from '../models/GenerateMappingsPayload';
import type { LensCaptureRead } from '../models/LensCaptureRead';
import type { LensPayload } from '../models/LensPayload';
import type { LensRead } from '../models/LensRead';
import type { ManualEntryRequest } from '../models/ManualEntryRequest';
import type { MappingPayload } from '../models/MappingPayload';
import type { MappingRead } from '../models/MappingRead';
import type { RenderableRichText } from '../models/RenderableRichText';
import type { RenderDataExtractionContextPayload } from '../models/RenderDataExtractionContextPayload';
import type { RenderedRichText } from '../models/RenderedRichText';
import type { SimulatedMappingResponse } from '../models/SimulatedMappingResponse';
import type { src__app__collection__data_extraction__router__LuChatPromptPayload } from '../models/src__app__collection__data_extraction__router__LuChatPromptPayload';
import type { UserPresenceHeartbeatPayload } from '../models/UserPresenceHeartbeatPayload';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class DataExtractionService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Get Mapping For Id
     * @param mappingId
     * @returns MappingRead Successful Response
     * @throws ApiError
     */
    public getMappingForId(
        mappingId: any,
    ): CancelablePromise<MappingRead> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/data-extraction/get-mapping-for-id/{mapping_id}',
            path: {
                'mapping_id': mappingId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Extraction Attempts For Property
     * @param propertyId
     * @param documentId
     * @returns ExtractionQueryAttemptRead Successful Response
     * @throws ApiError
     */
    public listExtractionAttemptsForProperty(
        propertyId: string,
        documentId: string,
    ): CancelablePromise<Array<ExtractionQueryAttemptRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/data-extraction/list-extraction-attempts-for-property-id/',
            query: {
                'property_id': propertyId,
                'document_id': documentId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Cancel Extraction Attempts
     * @param propertyId
     * @param documentId
     * @returns number Successful Response
     * @throws ApiError
     */
    public cancelExtractionAttempts(
        propertyId: string,
        documentId: string,
    ): CancelablePromise<number> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/data-extraction/cancel-extraction-attempts/',
            query: {
                'property_id': propertyId,
                'document_id': documentId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Manual Entry With Sources
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public manualEntryWithSources(
        requestBody: ManualEntryRequest,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/data-extraction/extraction-query-results/manual-entry',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Duplicate Lens
     * @param lensId
     * @param requestBody
     * @returns LensRead Successful Response
     * @throws ApiError
     */
    public duplicateLens(
        lensId: any,
        requestBody: DuplicateLensPayload,
    ): CancelablePromise<LensRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/data-extraction/duplicate-lens/{lens_id}',
            path: {
                'lens_id': lensId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Lens
     * @param lensId
     * @param requestBody
     * @returns LensRead Successful Response
     * @throws ApiError
     */
    public updateLens(
        lensId: any,
        requestBody: LensPayload,
    ): CancelablePromise<LensRead> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/data-extraction/update-lens/{lens_id}',
            path: {
                'lens_id': lensId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Lens
     * @param lensId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteLens(
        lensId: any,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/data-extraction/delete-lens/{lens_id}',
            path: {
                'lens_id': lensId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Mapping Templates For Lens
     * @param lensId
     * @returns MappingRead Successful Response
     * @throws ApiError
     */
    public listMappingTemplatesForLens(
        lensId: any,
    ): CancelablePromise<Array<MappingRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/data-extraction/list-mapping-templates-for-lens/{lens_id}',
            path: {
                'lens_id': lensId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Data Extraction
     * @param dataExtractionId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteDataExtraction(
        dataExtractionId: any,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/data-extraction/delete-data-extraction/{data_extraction_id}',
            path: {
                'data_extraction_id': dataExtractionId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Data Extraction Payload
     * @param dataExtractionId
     * @returns DataExtractionPayload Successful Response
     * @throws ApiError
     */
    public getDataExtractionPayload(
        dataExtractionId: any,
    ): CancelablePromise<DataExtractionPayload> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/data-extraction/get-data-extraction-payload/{data_extraction_id}',
            path: {
                'data_extraction_id': dataExtractionId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Reset Data Extraction
     * @param dataExtractionId
     * @returns any Successful Response
     * @throws ApiError
     */
    public resetDataExtraction(
        dataExtractionId: any,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/data-extraction/reset-data-extraction/{data_extraction_id}',
            path: {
                'data_extraction_id': dataExtractionId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Published Values
     * @param dataExtractionId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deletePublishedValues(
        dataExtractionId: any,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/data-extraction/delete-published-values/{data_extraction_id}',
            path: {
                'data_extraction_id': dataExtractionId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Data Extraction Presence
     * Get current presence state for a data extraction.
     *
     * Returns AI processing status and list of users currently viewing/editing.
     *
     * Async route: the blocking Redis reads are offloaded via ``run_in_threadpool``
     * so they do not block the event loop.
     * @param dataExtractionId
     * @returns DataExtractionPresenceRead Successful Response
     * @throws ApiError
     */
    public getDataExtractionPresence(
        dataExtractionId: any,
    ): CancelablePromise<DataExtractionPresenceRead> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/data-extraction/data-extraction/{data_extraction_id}/presence',
            path: {
                'data_extraction_id': dataExtractionId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Leave Presence
     * Clear user presence when leaving the data extraction.
     *
     * Called when the user navigates away from the extraction view.
     * @param dataExtractionId
     * @param entityId
     * @returns any Successful Response
     * @throws ApiError
     */
    public leavePresence(
        dataExtractionId: any,
        entityId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/data-extraction/data-extraction/{data_extraction_id}/presence',
            path: {
                'data_extraction_id': dataExtractionId,
            },
            query: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Heartbeat Presence
     * Heartbeat endpoint to maintain user presence.
     *
     * Should be called every 15 seconds by the frontend to indicate
     * the user is still viewing/editing the extraction.
     *
     * Async route: the blocking Redis/DB body is offloaded via ``run_in_threadpool``
     * (no async driver). That primitive is non-cancellable, so a cancelled request
     * cannot orphan the body while it holds the request's DB session.
     * @param dataExtractionId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public heartbeatPresence(
        dataExtractionId: any,
        requestBody: UserPresenceHeartbeatPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/data-extraction/data-extraction/{data_extraction_id}/presence/heartbeat',
            path: {
                'data_extraction_id': dataExtractionId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Cancel Ai Processing
     * Cancel AI processing for a data extraction.
     *
     * Signals the running task to stop and clears the AI presence.
     * @param dataExtractionId
     * @param entityId
     * @returns any Successful Response
     * @throws ApiError
     */
    public cancelAiProcessing(
        dataExtractionId: any,
        entityId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/data-extraction/data-extraction/{data_extraction_id}/cancel-ai',
            path: {
                'data_extraction_id': dataExtractionId,
            },
            query: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Lens
     * @param entityId
     * @param requestBody
     * @returns LensRead Successful Response
     * @throws ApiError
     */
    public createLens(
        entityId: string,
        requestBody: LensPayload,
    ): CancelablePromise<LensRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/data-extraction/create-lens',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Lenses For Entity
     * @param entityId
     * @returns LensPayload Successful Response
     * @throws ApiError
     */
    public listLensesForEntity(
        entityId: string,
    ): CancelablePromise<Array<LensPayload>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/data-extraction/list-lenses-for-entity',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Data Extraction Summaries For Entity
     * @param entityId
     * @returns DataExtractionSummary Successful Response
     * @throws ApiError
     */
    public listDataExtractionSummariesForEntity(
        entityId: string,
    ): CancelablePromise<Array<DataExtractionSummary>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/data-extraction/list-data-extraction-summaries-for-entity',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Previous Mappings For Lens Ids
     * @param entityId
     * @param dataExtractionId
     * @param requestBody
     * @param maximumEffectiveDate
     * @returns MappingPayload Successful Response
     * @throws ApiError
     */
    public listPreviousMappingsForLensIds(
        entityId: string,
        dataExtractionId: string,
        requestBody: Array<string>,
        maximumEffectiveDate?: (string | null),
    ): CancelablePromise<Array<MappingPayload>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/data-extraction/list-previous-mappings-for-lens-ids/{data_extraction_id}',
            path: {
                'entity_id': entityId,
                'data_extraction_id': dataExtractionId,
            },
            query: {
                'maximum_effective_date': maximumEffectiveDate,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Data Extraction
     * @param entityId
     * @param dataExtractionId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public saveDataExtraction(
        entityId: string,
        dataExtractionId: any,
        requestBody: DataExtractionPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/data-extraction/save-data-extraction',
            path: {
                'entity_id': entityId,
            },
            query: {
                'data_extraction_id': dataExtractionId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Data Extraction Context
     * @param entityId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public saveDataExtractionContext(
        entityId: string,
        requestBody: RenderableRichText,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/entities/{entity_id}/data-extraction/save-data-extraction-context',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Render Data Extraction Context
     * @param entityId
     * @param clusterId
     * @param namespaceId
     * @param relativeToDate
     * @param requestBody
     * @returns RenderedRichText Successful Response
     * @throws ApiError
     */
    public renderDataExtractionContext(
        entityId: string,
        clusterId: string,
        namespaceId: string,
        relativeToDate: string,
        requestBody: RenderDataExtractionContextPayload,
    ): CancelablePromise<RenderedRichText> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/data-extraction/render-data-extraction-context/{cluster_id}/{namespace_id}',
            path: {
                'entity_id': entityId,
                'cluster_id': clusterId,
                'namespace_id': namespaceId,
            },
            query: {
                'relative_to_date': relativeToDate,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Renderable Data Extraction Context
     * @param entityId
     * @returns RenderableRichText Successful Response
     * @throws ApiError
     */
    public getRenderableDataExtractionContext(
        entityId: string,
    ): CancelablePromise<RenderableRichText> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/data-extraction/get-renderable-data-extraction-context',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Calculate Data Extraction Results For Payload
     * @param entityId
     * @param dataExtractionId
     * @param requestBody
     * @param forceBustCache
     * @param focusedDocumentId
     * @returns DataExtractionResults Successful Response
     * @throws ApiError
     */
    public calculateDataExtractionResultsForPayload(
        entityId: string,
        dataExtractionId: any,
        requestBody: DataExtractionPayload,
        forceBustCache: boolean = false,
        focusedDocumentId?: (string | null),
    ): CancelablePromise<DataExtractionResults> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/data-extraction/calculate-data-extraction-results-for-payload/{data_extraction_id}',
            path: {
                'entity_id': entityId,
                'data_extraction_id': dataExtractionId,
            },
            query: {
                'force_bust_cache': forceBustCache,
                'focused_document_id': focusedDocumentId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Generate Mappings
     * @param entityId
     * @param requestBody
     * @returns AutoMappingAIPayload Successful Response
     * @throws ApiError
     */
    public generateMappings(
        entityId: string,
        requestBody: GenerateMappingsPayload,
    ): CancelablePromise<Array<AutoMappingAIPayload>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/data-extraction/generate-mappings',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Extract Fields From Document
     * Kick off field discovery as a background task.
     *
     * Sends the document to Gemini directly (no RAG/indexing needed).
     * Results are delivered via WebSocket on channel field_discovery:{entity_id}.
     * @param entityId
     * @param documentId
     * @param prompt
     * @returns FieldDiscoveryStartResponse Successful Response
     * @throws ApiError
     */
    public extractFieldsFromDocument(
        entityId: string,
        documentId: string,
        prompt?: (string | null),
    ): CancelablePromise<FieldDiscoveryStartResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/data-extraction/extract-fields-from-document',
            path: {
                'entity_id': entityId,
            },
            query: {
                'document_id': documentId,
                'prompt': prompt,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Extract With Lu
     * RAG chat endpoint with streaming via WebSocket
     * @param entityId
     * @param conversationId
     * @param dataExtractionId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public extractWithLu(
        entityId: string,
        conversationId: string,
        dataExtractionId: any,
        requestBody?: (src__app__collection__data_extraction__router__LuChatPromptPayload | null),
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/data-extraction/extract-with-lu/{conversation_id}/{data_extraction_id}',
            path: {
                'entity_id': entityId,
                'conversation_id': conversationId,
                'data_extraction_id': dataExtractionId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Or Update Ai Extraction Context
     * Create or update AI extraction context for an entity.
     * @param entityId
     * @param requestBody
     * @returns AIExtractionContextRead Successful Response
     * @throws ApiError
     */
    public createOrUpdateAiExtractionContext(
        entityId: string,
        requestBody: AIExtractionContextCreate,
    ): CancelablePromise<AIExtractionContextRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/data-extraction/ai-extraction-context',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Ai Extraction Context
     * Get AI extraction context for an entity.
     * @param entityId
     * @returns any Successful Response
     * @throws ApiError
     */
    public getAiExtractionContext(
        entityId: string,
    ): CancelablePromise<(AIExtractionContextRead | null)> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/data-extraction/ai-extraction-context',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Simulate Historical Mapping
     * Simulates historical mapping for the given entity using the provided mapping.
     *
     * Pass ?run_async=true to run in background via Dramatiq. When async, the worker sends one
     * WebSocket message on ``historical_mapping_simulation:{request_id}`` with payload
     * ``{status: completed, results: [...]}`` — the same list shape as the synchronous response.
     * Without the flag, returns that JSON array directly.
     * @param entityId
     * @param requestBody
     * @param runAsync
     * @returns any Successful Response
     * @throws ApiError
     */
    public simulateHistoricalMapping(
        entityId: string,
        requestBody: MappingPayload,
        runAsync: boolean = false,
    ): CancelablePromise<(Record<string, any> | Array<SimulatedMappingResponse>)> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/data-extraction/simulate-historical-mapping',
            path: {
                'entity_id': entityId,
            },
            query: {
                'run_async': runAsync,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Backfill Mapping
     * Backfills field values by running a mapping expression on historical data extractions.
     *
     * This endpoint creates a copy of the mapping for each specified data extraction
     * and saves the calculated field values.
     * @param entityId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public backfillMapping(
        entityId: string,
        requestBody: BackfillMappingPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/data-extraction/backfill-mapping',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Data Extraction Overview
     * @param clusterId
     * @returns DataExtractionOverview Successful Response
     * @throws ApiError
     */
    public getDataExtractionOverview(
        clusterId: string,
    ): CancelablePromise<DataExtractionOverview> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/data-extraction/get-data-extraction-overview',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Previous Lens Captures For Lens Ids
     * @param clusterId
     * @param dataExtractionId
     * @param maximumEffectiveDate
     * @param requestBody
     * @returns LensCaptureRead Successful Response
     * @throws ApiError
     */
    public listPreviousLensCapturesForLensIds(
        clusterId: string,
        dataExtractionId: string,
        maximumEffectiveDate: (string | null),
        requestBody: Array<string>,
    ): CancelablePromise<Array<LensCaptureRead>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/data-extraction/list-previous-lens-captures-for-lens_ids/{data_extraction_id}',
            path: {
                'cluster_id': clusterId,
                'data_extraction_id': dataExtractionId,
            },
            query: {
                'maximum_effective_date': maximumEffectiveDate,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Copy Rich Text Block
     * @param clusterId
     * @param requestBody
     * @returns CopiedRichTextResponse Successful Response
     * @throws ApiError
     */
    public copyRichTextBlock(
        clusterId: string,
        requestBody: CopiedRichTextPayload,
    ): CancelablePromise<CopiedRichTextResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/data-extraction/copy-rich-text-block',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
